// Host awareness for the marketing / app split.
//
// The same React bundle is served on two hostnames:
//   • the apex (e.g. holidayhive.co.uk / holidayhive.dev)  → MARKETING (landing page)
//   • the app subdomain (app.holidayhive.co.uk / .dev)     → the PRODUCT (login + app)
//
// getHostInfo() looks at window.location and tells the app which "surface" it's on
// plus the absolute origins of each surface, so we can cross-link / cross-redirect.
//
// Plain localhost (or 127.0.0.1) is treated as the APP surface so the classic
// `localhost:3000` dev workflow still runs the whole product. To exercise the
// split locally, use the browser-native *.localhost hostnames instead:
//   • http://holidayhive.localhost:3000       (marketing)
//   • http://app.holidayhive.localhost:3000   (app)

// ⚠️ PRE-LAUNCH (12/09/2026, Rob's option A). On these apex hosts the marketing surface
// shows the "register your interest" page instead of the full landing page, because the
// app is not served on that domain yet and a page full of "Get started" buttons that go
// nowhere reads as a broken product. The .dev apex is NOT listed on purpose: it keeps the
// real landing page so it can still be worked on. Remove a host here on launch day.
export const PRELAUNCH_HOSTS = ['holidayhive.co.uk', 'www.holidayhive.co.uk',
  'prelaunch.localhost'];  // the last one only exists so the page can be opened in local dev

export function isPrelaunchHost(hostname = window.location.hostname) {
  return PRELAUNCH_HOSTS.includes(String(hostname || '').toLowerCase());
}

// Where the marketing surface should send API calls. On a real host the API is on the
// SAME origin (the edge proxies /api to the backend on every hostname it serves), which
// sidesteps CORS for the pre-launch form on holidayhive.co.uk — a host the backend's
// CORS list does not know. On the *.localhost dev hosts there is no proxy, so the dev
// backend's absolute URL is used instead.
export function marketingApiBase() {
  const { hostname } = window.location;
  const isLocal = hostname === 'localhost' || hostname.endsWith('.localhost') || /^127(?:\.\d{1,3}){3}$/.test(hostname);
  return isLocal ? (process.env.REACT_APP_BACKEND_URL || '') : '';
}

export function getHostInfo() {
  const { protocol, hostname, port } = window.location;

  // Bare local hosts keep the single-origin behaviour (full app on localhost:3000).
  const isBareLocal =
    hostname === 'localhost' ||
    hostname === '[::1]' ||
    /^127(?:\.\d{1,3}){3}$/.test(hostname);

  const isAppSubdomain = hostname.startsWith('app.');
  const isApp = isAppSubdomain || isBareLocal;

  // Work out the apex (marketing) host by stripping a leading app. / www.
  let apexHost = hostname;
  if (isAppSubdomain) apexHost = hostname.slice(4);
  else if (hostname.startsWith('www.')) apexHost = hostname.slice(4);

  const portSuffix = port ? `:${port}` : '';

  // On bare localhost there IS no marketing surface on this origin (/, like every
  // other route, belongs to the app there) — the landing page lives on the
  // browser-native *.localhost host instead. Point marketing links at it so
  // "back to the homepage" works in local dev exactly like it does in production.
  const marketingHost = isBareLocal ? 'holidayhive.localhost' : apexHost;

  return {
    isApp,
    isBareLocal,
    apexHost,
    appOrigin: `${protocol}//app.${apexHost}${portSuffix}`,
    marketingOrigin: `${protocol}//${marketingHost}${portSuffix}`,
  };
}
